import React from 'react';
import { Form, Input, Modal, Select, message } from 'antd';
import { TTeamMemberDto, TRole, TScopedProviderOption } from 'types/group';
import { ERoleCode } from 'types/enumItems';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { REQUIRED_FIELD } from 'constants/common';
import UsPhoneInput from 'components/form/USPhoneInput';
import FormValidationHelper from 'utils/validation';
import { addStaff, editStaff, checkStaffEmail, getScopedProviderOptions } from 'api/group';
import { updateStaffMembership, getOpsTeamMemberRoles } from 'api/operation/group';
import useAllPlatformRoles from 'hooks/group/useAllPlatformRoles';
import useAllTeamMember from 'hooks/group/useAllTeamMember';

export type OpsEditConfig = {
    userId: number;
    memberId: number;
    providerOptions: { label: string; value: string }[];
};

type Props = {
    show: boolean;
    item?: TTeamMemberDto;
    onCannel?: (needRefresh?: boolean) => void;
    onSuccess?: (id?: number) => void;
    wrapClassName?: string;
    isOps?: boolean;
    organizationId?: number;
    opsConfig?: OpsEditConfig;
};

type RoleConfig = {
    key: ERoleCode;
    name: string;
    icon: React.ReactNode;
    description: string;
};

type PermissionCategory = {
    title: string;
    items: string[];
};

const ROLE_PERMISSIONS: Partial<Record<ERoleCode, PermissionCategory[]>> = {
    [ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Billing & payments',
            items: ['Invoices', 'Financials (Payouts & marketing investment)'],
        },
    ],
    [ERoleCode.GROUP_CLINICAL_ASSISTANT]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Clinical care',
            items: ['Treatment plans', 'Prescriptions', 'Text visits'],
        },
    ],
    [ERoleCode.GROUP_PRACTICE_MANAGER]: [
        {
            title: 'Patient care',
            items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
        },
        {
            title: 'Billing & payments',
            items: ['Invoices', 'Financials (Payouts & marketing investment)'],
        },
        {
            title: 'Practice management',
            items: [
                'Homepage',
                'Practice profile',
                'Service templates',
                'Medications settings',
                'Marketing (Pro-listings)',
                'Appointment policies (No-shows, late cancellations and rescheduling)',
            ],
        },
        {
            title: 'Team & access',
            items: ['Add providers', 'Settings'],
        },
    ],
};

const AdminAssistantIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M2.5 13.5V3.5C2.5 2.94772 2.94772 2.5 3.5 2.5H10.5L13.5 5.5V13.5C13.5 14.0523 13.0523 14.5 12.5 14.5H3.5C2.94772 14.5 2.5 14.0523 2.5 13.5Z"
            stroke="#374151"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M5.5 8.5H10.5" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M5.5 11H10.5" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
);

const ClinicalAssistantIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M6.1331 15.666C5.72979 15.666 5.38465 15.5225 5.09768 15.2356C4.81072 14.9486 4.66699 14.6032 4.6665 14.1994V11.9995H2.4666C2.06329 11.9995 1.71815 11.856 1.43118 11.5691C1.14422 11.2821 1.00049 10.9367 1 10.5329V6.1331C1 5.72979 1.14373 5.38465 1.43118 5.09768C1.71863 4.81072 2.06377 4.66699 2.4666 4.6665H4.6665V2.4666C4.6665 2.06329 4.81023 1.71815 5.09768 1.43118C5.38514 1.14422 5.73028 1.00049 6.1331 1H10.5329C10.9362 1 11.2816 1.14373 11.5691 1.43118C11.8565 1.71863 12 2.06377 11.9995 2.4666V4.6665H14.1994C14.6027 4.6665 14.9481 4.81023 15.2356 5.09768C15.523 5.38514 15.6665 5.73028 15.666 6.1331V10.5329C15.666 10.9362 15.5225 11.2816 15.2356 11.5691C14.9486 11.8565 14.6032 12 14.1994 11.9995H11.9995V14.1994C11.9995 14.6027 11.856 14.9481 11.5691 15.2356C11.2821 15.523 10.9367 15.6665 10.5329 15.666H6.1331ZM2.4666 7.59971H6.1331C6.25532 7.59971 6.37143 7.63026 6.48142 7.69137C6.59142 7.75248 6.67697 7.83192 6.73808 7.92969L7.37972 8.88298L8.36967 5.91311C8.41856 5.76645 8.50704 5.64424 8.63513 5.54646C8.76321 5.44869 8.90694 5.3998 9.06631 5.3998C9.18852 5.3998 9.30463 5.43036 9.41463 5.49147C9.52462 5.55258 9.61017 5.63202 9.67128 5.72979L10.9179 7.59971H14.1994V6.1331H11.2662C11.0584 6.1331 10.8844 6.06271 10.7441 5.92191C10.6038 5.78112 10.5334 5.60708 10.5329 5.3998V2.4666H6.1331V5.3998C6.1331 5.60757 6.06271 5.78185 5.92191 5.92265C5.78112 6.06344 5.60708 6.13359 5.3998 6.1331H2.4666V7.59971ZM2.4666 9.06631V10.5329H5.3998C5.60757 10.5329 5.78185 10.6033 5.92265 10.7441C6.06344 10.8849 6.13359 11.0589 6.1331 11.2662V14.1994H10.5329V11.2662C10.5329 11.0584 10.6033 10.8844 10.7441 10.7441C10.8849 10.6038 11.0589 10.5334 11.2662 10.5329H14.1994V9.06631H10.5329C10.4107 9.06631 10.2946 9.03575 10.1846 8.97465C10.0746 8.91354 9.98293 8.8341 9.9096 8.73632L9.2863 7.78303L8.29634 10.7529C8.24746 10.8996 8.15579 11.0218 8.02135 11.1195C7.88692 11.2173 7.74026 11.2662 7.58137 11.2662C7.45916 11.2662 7.34305 11.2357 7.23306 11.1745C7.12306 11.1134 7.03751 11.034 6.9764 10.9362L5.72979 9.06631H2.4666Z"
            fill="#374151"
        />
    </svg>
);

const PracticeManagerIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="4.5" stroke="#374151" strokeWidth="1.2" />
        <path d="M9.5 9.5L14.5 14.5" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M4 6H8" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M6 4V8" stroke="#374151" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9L12 15L18 9" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ROLE_CONFIGS: RoleConfig[] = [
    {
        key: ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
        name: 'Administrative assistant',
        icon: <AdminAssistantIcon />,
        description: 'Supports front-desk operations including scheduling, patient communication, and billing.',
    },
    {
        key: ERoleCode.GROUP_CLINICAL_ASSISTANT,
        name: 'Clinical assistant (MA)',
        icon: <ClinicalAssistantIcon />,
        description: 'Supports providers with clinical documentation, labs, and prescriptions.',
    },
    {
        key: ERoleCode.GROUP_PRACTICE_MANAGER,
        name: 'Practice manager',
        icon: <PracticeManagerIcon />,
        description:
            'Full access to practice operations, billing, and settings. Manages team workflows and administrative tools.',
    },
];

const ALL_PROVIDERS_OPTION = { label: 'All providers', value: 'all' };

const PermissionCategoryPanel: React.FC<{ category: PermissionCategory }> = ({ category }) => {
    const [open, setOpen] = React.useState(true);

    return (
        <div className={s.permissionCategory}>
            <div className={s.categoryHeader} onClick={() => setOpen(!open)}>
                <span className={s.categoryTitle}>{category.title}</span>
                <span className={`${s.chevron} ${open ? s.chevronOpen : ''}`}>
                    <ChevronDownIcon />
                </span>
            </div>
            {open && (
                <div className={s.categoryItems}>
                    {category.items.map((item) => (
                        <div key={item} className={s.categoryItem}>
                            {item}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const AddAndEditStaff = (props: Props) => {
    const { show, onCannel, item, wrapClassName, isOps, opsConfig } = props;
    const [submiting, setSubmiting] = React.useState(false);
    const [form] = Form.useForm();
    const [portalRoles] = useAllPlatformRoles(!isOps);
    const [opsRoles, setOpsRoles] = React.useState<TRole[]>([]);
    // SELECTABLE providers for the visibility-scope picker. Must come from
    // /provider-options (ACTIVE members only, GROUP_OWNER included) — NOT from the
    // team-member listPage, which drops the owner and keeps PENDING/never-invited
    // providers. A scope pointing at a non-ACTIVE provider locks the staff out of login.
    const [scopedProviders, setScopedProviders] = React.useState<TScopedProviderOption[]>([]);
    // Full roster, used ONLY to render the non-selectable rows (greyed out, with a reason)
    // so a pending colleague is visibly "not yet assignable" rather than silently absent.
    const [portalMembers] = useAllTeamMember(false, !isOps);
    const [providersLoading, setProvidersLoading] = React.useState(false);
    const [providersFailed, setProvidersFailed] = React.useState(false);

    React.useEffect(() => {
        if (isOps) {
            getOpsTeamMemberRoles().then((res) => {
                if (res && !res.error) {
                    setOpsRoles(res.data?.data || []);
                }
            });
        }
    }, [isOps]);

    React.useEffect(() => {
        if (isOps || !show) return;
        setProvidersLoading(true);
        setProvidersFailed(false);
        getScopedProviderOptions({ includeSelf: true })
            .then((res) => {
                if (res && !res.error) {
                    setScopedProviders(res.data?.data || []);
                } else {
                    // Do NOT fail silently. An empty picker is indistinguishable from
                    // "this practice has no providers", and the natural next move is to
                    // save with "All providers" — over-granting access to the whole
                    // practice's PHI. Surface the failure and block submit instead.
                    setProvidersFailed(true);
                }
            })
            .catch(() => setProvidersFailed(true))
            .finally(() => setProvidersLoading(false));
    }, [isOps, show]);

    const allRoles = isOps ? opsRoles : portalRoles;
    const isEdit = !!item?.id;
    const [selectedRole, setSelectedRole] = React.useState<ERoleCode>(ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT);
    const [isAllProviders, setIsAllProviders] = React.useState(true);
    const [assignedProviderIds, setAssignedProviderIds] = React.useState<number[]>([]);

    React.useEffect(() => {
        if (show && item) {
            form.setFieldsValue({
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.email,
                phoneNumber: item.phoneNumber,
            });
            if (item.roleCode) setSelectedRole(item.roleCode as ERoleCode);
            setIsAllProviders(item.isAllProviders);
            if (!item.isAllProviders && item.assignedProviders?.length) {
                setAssignedProviderIds(item.assignedProviders.map((p) => p.providerId));
            } else {
                setAssignedProviderIds([]);
            }
        }
        if (!show) {
            form.resetFields();
            setSelectedRole(ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT);
            setIsAllProviders(true);
            setAssignedProviderIds([]);
        }
    }, [show, item, form]);

    const providerOptions = React.useMemo(() => {
        if (isOps && opsConfig?.providerOptions) {
            return [ALL_PROVIDERS_OPTION, ...opsConfig.providerOptions];
        }
        // Selectable set — the backend owns this rule (/provider-options is
        // `status='ACTIVE' AND role IN (GROUP_OWNER, GROUP_PROVIDER)`). Do not re-derive it
        // here; the Ops-side picker got this wrong precisely by judging it in the frontend.
        const providers = scopedProviders.map((p) => ({
            label: p.credential ? `${p.firstName} ${p.lastName}, ${p.credential}` : `${p.firstName} ${p.lastName}`,
            value: String(p.providerId),
        }));
        const known = new Set(providers.map((o) => o.value));

        // Anything the practice has that is NOT selectable still gets rendered, greyed out,
        // with the reason. Hiding it outright leaves the admin wondering why the colleague
        // they just invited is missing and gives them no idea what to do about it; a
        // disabled row says "this person exists, finish their invite first".
        const blocked = new Map<string, string>();
        portalMembers.forEach((m) => {
            const pid = m.providerId == null ? '' : String(m.providerId);
            if (!pid || known.has(pid)) return;
            if (m.roleCode !== 'GROUP_PROVIDER' && m.roleCode !== 'GROUP_OWNER') return;
            if (m.status === 'DISABLED') return; // removed from the practice — not a real choice
            const name = `${m.firstName || ''} ${m.lastName || ''}`.trim() || `Provider #${pid}`;
            blocked.set(pid, `${name} — invite pending, can't be assigned yet`);
        });

        // Already-saved assignments to a provider that is no longer selectable. Without an
        // option antd renders the raw id as the tag label ("11034"), and Save resubmits an id
        // the backend then drops — the admin walks away thinking the scope was kept.
        (item?.assignedProviders || []).forEach((p) => {
            const pid = String(p.providerId);
            if (known.has(pid) || blocked.has(pid)) return;
            const name = `${p.firstName || ''} ${p.lastName || ''}`.trim() || `Provider #${pid}`;
            blocked.set(pid, `${name} — no longer active, will be removed on save`);
        });

        const blockedOptions = Array.from(blocked.entries()).map(([value, label]) => ({
            label,
            value,
            disabled: true,
        }));
        return [ALL_PROVIDERS_OPTION, ...providers, ...blockedOptions];
    }, [scopedProviders, portalMembers, isOps, opsConfig, item]);

    const permissions = selectedRole ? ROLE_PERMISSIONS[selectedRole] || [] : [];

    const onSubmit = async () => {
        try {
            const formValues = await form.validateFields();

            const matchedRole = allRoles.find((r) => r.name === selectedRole);
            if (!matchedRole) {
                message.error('Invalid role selected');
                return;
            }
            const roleId = matchedRole.id;
            const isAdmin = selectedRole === ERoleCode.GROUP_PRACTICE_MANAGER;
            // Guard the over-grant path: if the provider list failed to load, "All providers"
            // is the state the form defaults to, not a choice the admin actually made.
            if (providersFailed && isAllProviders) {
                message.error("Couldn't load the provider list. Reopen this dialog and try again.");
                return;
            }
            const providerIds = isAllProviders ? [] : assignedProviderIds;

            setSubmiting(true);

            if (isOps && opsConfig) {
                const res = await updateStaffMembership(opsConfig.userId, opsConfig.memberId, {
                    roleId,
                    isAdmin,
                    phoneNumber: formValues.phoneNumber || null,
                    providerIds: isAllProviders ? null : providerIds,
                });
                if (res.error) return;
                message.success('Team member updated');
                props.onSuccess?.();
                return;
            }

            if (isEdit && item?.id) {
                const res = await editStaff(item.id, {
                    firstName: formValues.firstName,
                    lastName: formValues.lastName,
                    phoneNumber: formValues.phoneNumber,
                    roleId,
                    isAdmin,
                    providerIds,
                });
                if (res.error) return;
                message.success('Team member updated');
                props.onSuccess?.(item.id);
                return;
            }

            const res = await addStaff({
                firstName: formValues.firstName,
                lastName: formValues.lastName,
                email: formValues.email,
                phoneNumber: formValues.phoneNumber,
                roleId,
                isAdmin,
                providerIds,
            });

            if (res.error) return;

            message.success('Invitation sent');
            props.onSuccess?.();
        } catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e) {
                return;
            }
        } finally {
            setSubmiting(false);
        }
    };

    return (
        <Modal
            title={isEdit ? 'Edit staff' : 'Add staff'}
            closable
            className={commonS.modalFixHeightWrap}
            width="810px"
            onCancel={() => {
                onCannel?.();
            }}
            okText={isEdit ? 'Save' : 'Send invite'}
            cancelText="Cancel"
            open={show}
            onOk={onSubmit}
            destroyOnClose
            okButtonProps={{ loading: submiting }}
            zIndex={99999}
            wrapClassName={wrapClassName || ''}
        >
            <Form
                form={form}
                name="teamMember"
                className={commonS.formStyle1}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <div className={s.modalWrap}>
                    <h3 className={s.sectionTitle}>Basic information</h3>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="First name"
                                name="firstName"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input maxLength={25} disabled={isOps} />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Last name"
                                name="lastName"
                                rules={[{ required: true, message: REQUIRED_FIELD }]}
                            >
                                <Input maxLength={25} disabled={isOps} />
                            </Form.Item>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Email"
                                name="email"
                                validateFirst
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                    { required: !isEdit && !isOps, message: REQUIRED_FIELD },
                                    { type: 'email', message: 'Please enter a valid email' },
                                    {
                                        validateTrigger: 'onBlur',
                                        validator: async (_, value) => {
                                            if (isEdit || isOps || !value?.trim()) return;
                                            const { data } = await checkStaffEmail(value.trim());
                                            if (data?.code !== 200 || !data?.data) return;
                                            const result = data.data;
                                            if (!result.addable) {
                                                throw new Error(result.message || 'This email cannot be added');
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Input disabled={isEdit || isOps} />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '368px' }}
                                label="Phone number"
                                name="phoneNumber"
                                validateFirst
                                rules={[
                                    FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                                ]}
                            >
                                <UsPhoneInput />
                            </Form.Item>
                        </div>
                    </div>

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Select role</h3>
                    <div className={s.roleGrid}>
                        {ROLE_CONFIGS.map((role) => {
                            const isSelected = selectedRole === role.key;
                            const cardClass = [s.roleCard, isSelected ? s.roleCardSelected : '']
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <div
                                    key={role.key}
                                    className={cardClass}
                                    onClick={() => {
                                        setSelectedRole(role.key);
                                    }}
                                >
                                    <div className={s.roleCardHeader}>
                                        <span className={s.roleIcon}>{role.icon}</span>
                                        <span className={s.roleName}>{role.name}</span>
                                    </div>
                                    <div className={s.roleDescription}>{role.description}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Permissions by category</h3>
                    {permissions.length > 0 ? (
                        <div className={s.permissionsSection}>
                            {permissions.map((category) => (
                                <PermissionCategoryPanel key={category.title} category={category} />
                            ))}
                        </div>
                    ) : (
                        <div className={s.noPermissions}>Select a role to view permissions</div>
                    )}

                    <div className={s.divider} />

                    <h3 className={s.sectionTitle}>Provider assignment</h3>
                    <p className={s.providerAssignDesc}>Select which providers this team member can access.</p>
                    <div className={s.providerAssignField}>
                        <div className={s.providerAssignLabel}>Assign to provider</div>
                        <Select
                            style={{ width: '100%' }}
                            mode="multiple"
                            value={isAllProviders ? ['all'] : assignedProviderIds.map(String)}
                            onChange={(values: string[]) => {
                                const wasAll = isAllProviders;
                                const nowHasAll = values.includes('all');
                                if (nowHasAll && !wasAll) {
                                    setIsAllProviders(true);
                                    setAssignedProviderIds([]);
                                } else if (wasAll && values.length > 1) {
                                    const filtered = values.filter((v) => v !== 'all');
                                    setIsAllProviders(false);
                                    setAssignedProviderIds(filtered.map(Number));
                                } else if (!nowHasAll) {
                                    setIsAllProviders(false);
                                    setAssignedProviderIds(values.map(Number));
                                }
                            }}
                            options={providerOptions}
                            loading={providersLoading}
                            placeholder={
                                providersFailed ? "Couldn't load providers — reopen to retry" : 'Select providers'
                            }
                            status={providersFailed ? 'error' : undefined}
                        />
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default AddAndEditStaff;
